import { DocumentDuplicateIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { omit, orderBy } from 'lodash'
import outdent from 'outdent'
import { MouseEvent, useEffect, useState } from 'react'
import { Link, NavLink, Redirect, Route, Switch, useHistory } from 'react-router-dom'

import useDatasource from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/hooks/useDatasource'
import { renderHelpText, requiredActionKeys } from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/utilities/constants'
import cn from '@/utilities/cn'

import { Metadata } from './Metadata'
import { Model } from './Model'
import { Perform } from './Perform'

export function Events() {
  const [datasource, setDatasource] = useDatasource(true)

  const [activeEventActionKey, setActiveEventActionKey] = useState<string>(() => {
    const item = localStorage.getItem(`${datasource.id}-active-event-action`)
    return item && Object.keys(datasource.events).includes(item) ? item : ''
  })

  const [activeTab, setActiveTab] = useState<string>(
    () => localStorage.getItem(`${datasource.id}-active-event-action-tab`) ?? ''
  )

  const datasourceEvents: typeof datasource.events = datasource.events

  const history = useHistory()

  useEffect(() => {
    return history.listen((location, action) => {
      if (action !== 'PUSH') return
      const selectedTab = location.pathname.split('/')[2]
      if (selectedTab) {
        localStorage.setItem(`${datasource.id}-active-event-action-tab`, selectedTab)
        setActiveTab(selectedTab)
      }

      const selectedAction = location.search.split('?eventAction=')[1]
      if (selectedAction) {
        localStorage.setItem(`${datasource.id}-active-event-action`, selectedAction)
        setActiveEventActionKey(selectedAction)
      }
    })
  }, [history, datasource.id])

  const orderedEventActions = orderBy(Object.keys(datasourceEvents || {}), value => value.toLowerCase())

  const handleAdd = () => {
    const eventAction = '__new-event-action__'

    setDatasource(datasource => ({
      ...datasource,
      events: {
        ...datasource.events,
        [eventAction]: {
          inputs: {
            type: 'object',
            properties: {
              lastPollTime: { type: 'string', format: 'date-time' }
            },
            required: []
          },
          perform: outdent`
            ${renderHelpText()}

            const pageSize = 100
            const pageField = 'PAGE_FIELD'
            const pageOffsetField = 'PAGE_OFFSET_FIELD'
            const queryField = 'QUERY_FIELD'
            const lastPollTime = inputs.lastPollTime
            const filterCondition = \`FIELD>\${lastPollTime}\`
            const queryCondition = {
              [queryField]: filterCondition
            }
            const path = 'PATH'
            const url = \`\${inputs.baseUrl}\${path}\`

            async function fetchRecords(page) {
             const searchParams = {
              [pageOffsetField]: page,
              [pageField]: pageSize.toString(),
              ...queryCondition
             }
             const records = await client.request({ url, responseType: 'json', responsePath: 'RESPONSE_PATH', searchParams })
             return records

            }

            // Fetch and process records until there are no more records
            let records = []
            let allRecords = []
            let page = 0

            do {
              records = await fetchRecords(page)
              allRecords = allRecords.concat(records)
              // Increment the pageSize to fetch the next set of records
              page += records.length

            } while (records.length > 0 && !inputs?.isTestExecution)

            export default allRecords
          `
        }
      }
    }))

    history.push(`/events/metadata?eventAction=${eventAction}`)
  }

  const handleDuplicate = (e: MouseEvent, eventKey: string) => {
    e.preventDefault()

    const eventAction = `${eventKey}_copy`

    setDatasource(datasource => ({
      ...datasource,
      events: {
        ...datasource.events,
        [eventAction]: datasourceEvents[eventKey]
      }
    }))

    history.push(`/events/metadata?eventAction=${eventAction}`)
  }

  const handleRemove = (event: MouseEvent, eventActionKey: string) => {
    event.preventDefault()

    const filteredEvents = omit(datasource.events, [eventActionKey])

    setDatasource(datasource => ({
      ...datasource,
      events: filteredEvents
    }))

    if (Object.keys(filteredEvents).length === 0) {
      setActiveEventActionKey('')
      setActiveTab('')
      history.push('/events')
      return
    }

    const deletedIndex = orderedEventActions.indexOf(eventActionKey)
    const newIndex = deletedIndex === orderedEventActions.length - 1 ? deletedIndex - 1 : deletedIndex

    history.push(`/events?eventAction=${orderedEventActions.filter(a => a !== eventActionKey)[newIndex]}`)
  }

  const handleMetadataChange = (key: string) => {
    localStorage.setItem(`${datasource.id}-active-event-action`, key)
    setActiveEventActionKey(key)
  }

  return (
    <div className="flex flex-1 divide-x">
      <div className="flex w-96 flex-shrink-0 p-4">
        <nav className="flex h-[calc(100vh-270px)] flex-1 flex-col">
          <div className="overflow-auto">
            <ul className="-mx-2 flex-1 space-y-1">
              {orderedEventActions.map(eventActionKey => (
                <li key={eventActionKey}>
                  <Link
                    to={`/events?eventAction=${eventActionKey}`}
                    className={cn(
                      'flex justify-between gap-x-3 rounded-md py-2 px-3',
                      activeEventActionKey === eventActionKey
                        ? 'bg-gray-50 text-blue-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                    )}
                  >
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold leading-6">
                      {datasourceEvents[eventActionKey].name || eventActionKey}
                    </span>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        title="Duplicate"
                        className="flex items-center"
                        onClick={event => handleDuplicate(event, eventActionKey)}
                      >
                        <DocumentDuplicateIcon
                          className="h-5 w-5 text-gray-400 hover:text-gray-500"
                          aria-hidden="true"
                        />
                      </button>

                      <button
                        type="button"
                        title={'Remove'}
                        className={cn(
                          'flex items-center rounded-md bg-red-600 px-1 shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600',
                          {
                            'bg-red-300 hover:bg-red-300': requiredActionKeys.has(eventActionKey)
                          }
                        )}
                        onClick={event => handleRemove(event, eventActionKey)}
                      >
                        <XMarkIcon className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            className="flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            onClick={handleAdd}
          >
            Add Event Action
          </button>
        </nav>
      </div>

      <div className="flex flex-1 flex-col">
        {activeEventActionKey ? (
          <>
            <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:px-6 lg:px-8">
              <nav>
                <ul className="flex gap-x-6 text-sm font-semibold leading-6">
                  <li>
                    <NavLink
                      to={`/events/metadata?eventAction=${activeEventActionKey}`}
                      className="text-gray-400"
                      activeClassName="text-blue-600"
                    >
                      Metadata
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={`/events/perform?eventAction=${activeEventActionKey}`}
                      className="text-gray-400"
                      activeClassName="text-blue-600"
                    >
                      Perform
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={`/events/response?eventAction=${activeEventActionKey}`}
                      className="text-gray-400"
                      activeClassName="text-blue-600"
                    >
                      Event Model
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to={`/test?action=${activeEventActionKey}`}
                      className="text-gray-400"
                      activeClassName="text-blue-600"
                    >
                      Test
                    </NavLink>
                  </li>
                </ul>
              </nav>
            </div>
            <div className="flex flex-1">
              <Switch>
                <Route path="/events/metadata" render={() => <Metadata onChange={handleMetadataChange} />} />
                <Route path="/events/perform" render={() => <Perform />} />
                <Route path="/events/response" render={() => <Model />} />
                <Redirect to={`/events/${activeTab}?eventAction=${activeEventActionKey}`} />
              </Switch>
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}
